import React, { Fragment } from "react";
import { Controller, useForm } from "react-hook-form";
import { PDFSettings } from "../../../../core/api/models";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import validator from "validator";

const UpdateForm: React.FunctionComponent<{
  onSubmit: (data: PDFSettings) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  data?: PDFSettings;
  error?: string;
}> = (props) => {
  const { control, handleSubmit, errors, watch } = useForm<PDFSettings>({
    reValidateMode: "onChange",
    mode: "onSubmit",
    shouldUnregister: true,
    defaultValues: props.data,
  });

  const renderShowInsurancePlanRejectedByCustomer = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <Controller
            control={control}
            name="showInsurancePlanRejectedByCustomer"
            defaultValue={props.data?.showInsurancePlanRejectedByCustomer}
            render={({ onChange, value, name }) => (
              <Fragment>
                <input
                  onChange={(e) => {
                    onChange(e.target.checked);
                  }}
                  type="checkbox"
                  name=""
                  id="showInsurancePlanRejectedByCustomer"
                  checked={value}
                />
                <label htmlFor="showInsurancePlanRejectedByCustomer">
                  Show Insurance Plan Rejected By Customer
                </label>
              </Fragment>
            )}
          />
        </div>
      </div>
    );
  };

  const renderShowInsuranceProposalForm = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <Controller
            control={control}
            name="showInsuranceProposalForm"
            defaultValue={props.data?.showInsuranceProposalForm}
            render={({ onChange, value, name }) => (
              <Fragment>
                <input
                  onChange={(e) => {
                    onChange(e.target.checked);
                  }}
                  type="checkbox"
                  name=""
                  id="showInsuranceProposalForm"
                  checked={value}
                />
                <label htmlFor="showInsuranceProposalForm">
                  Show Insurance Proposal Form On Quotation
                </label>
              </Fragment>
            )}
          />
        </div>
      </div>
    );
  };

  const renderShowDisclaimerOnConfigurationHasBooking = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <Controller
            control={control}
            name="showDisclaimerOnConfigurationHasBooking"
            defaultValue={props.data?.showDisclaimerOnConfigurationHasBooking}
            render={({ onChange, value, name }) => (
              <Fragment>
                <input
                  onChange={(e) => {
                    onChange(e.target.checked);
                  }}
                  type="checkbox"
                  name=""
                  id="showDisclaimerOnConfigurationHasBooking"
                  checked={value}
                />
                <label htmlFor="showDisclaimerOnConfigurationHasBooking">
                  Show Disclaimer On Configuration Has A Booking
                </label>
              </Fragment>
            )}
          />
        </div>
      </div>
    );
  };

  const renderPaidCapitalAmount = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Paid Up Capital Amount<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{
              required: "Paid Up Capital Amount is required",
            }}
            name="paidCapitalAmount"
            defaultValue={props.data?.paidCapitalAmount}
            render={({ onChange, value, name }) => (
              <input
                type="number"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Paid Up Capital Amount"
                name={name}
                value={value}
                onChange={(e) => {
                  onChange(parseFloat(e.target.value));
                }}
                id="paidCapitalAmount"
                min={0}
              />
            )}
          />
          <ErrorMessage>{errors.paidCapitalAmount?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderPrimaryColor = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Primary Color<span className="text-danger">*</span></label>
          <Controller
            control={control}
            rules={{
              required: "Primary Color is required",
            }}
            name="primaryColor"
            defaultValue={props.data?.primaryColor}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Primary Color"
                name={name}
                value={value}
                onChange={onChange}
                id="primaryColor"
              />
            )}
          />
          <ErrorMessage>{errors.primaryColor?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderRegistrationNumber = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Registration Number<span className="text-danger">*</span></label>
          <Controller
            control={control}
            rules={{
              required: "Registration Number is required",
            }}
            name="registrationNumber"
            defaultValue={props.data?.registrationNumber}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Registration Number"
                name={name}
                value={value}
                onChange={onChange}
                id="registrationNumber"
              />
            )}
          />
          <ErrorMessage>{errors.registrationNumber?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderCompanyNameEnglish = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Company Name in English<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{
              required: "Company Name in English Number is required",
            }}
            name="companyNameEnglish"
            defaultValue={props.data?.companyNameEnglish}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Company Name in English"
                name={name}
                value={value}
                onChange={onChange}
                id="companyNameEnglish"
              />
            )}
          />
          <ErrorMessage>{errors.companyNameEnglish?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderCompanyNameArabic = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            Company Name in Arabic<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{
              required: "Company Name in Arabic is required",
            }}
            name="companyNameArabic"
            defaultValue={props.data?.companyNameArabic}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Company Name in Arabic"
                name={name}
                value={value}
                onChange={onChange}
                id="companyNameArabic"
              />
            )}
          />
          <ErrorMessage>{errors.companyNameArabic?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderPdfLogoUrl = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">PDF Logo Url</label>
          <Controller
            control={control}
            rules={{
              validate: {
                validUrl: (value) =>
                  value && (validator.isURL(value) || "Invalid URL"),
              },
            }}
            name="logoUrl"
            defaultValue={props.data?.logoUrl}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Brand Logo Url"
                name={name}
                value={value}
                onChange={onChange}
                id="logoUrl"
              />
            )}
          />
          <ErrorMessage>{errors.logoUrl?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderWappPdfLogo = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">
            WhatsApp Pdf Logo Url
          </label>
          <Controller
            control={control}
            rules={{
              validate: {
                validUrl: (value) =>
                  value && (validator.isURL(value) || "Invalid URL"),
              },
            }}
            name="wappPdfLogo"
            defaultValue={props.data?.wappPdfLogo}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="WhatsApp Pdf Logo Url"
                name={name}
                value={value}
                onChange={onChange}
                id="wappPdfLogo"
              />
            )}
          />
          <ErrorMessage>{errors.wappPdfLogo?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderAlghanimLogoUrl = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Alghanim Logo Url</label>
          <Controller
            control={control}
            rules={{
              validate: {
                validUrl: (value) =>
                  value && (validator.isURL(value) || "Invalid URL"),
              },
            }}
            name="alghanimLogoUrl"
            defaultValue={props.data?.alghanimLogoUrl}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Brand Logo Url"
                name={name}
                value={value}
                onChange={onChange}
                id="alghanimLogoUrl"
              />
            )}
          />
          <ErrorMessage>{errors.alghanimLogoUrl?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderServiceCenterUrl = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3">
          <label className="font-sm text-uppercase">Service Center Url</label>
          <Controller
            control={control}
            rules={{
              validate: {
                validUrl: (value) =>
                  value && (validator.isURL(value) || "Invalid URL"),
              },
            }}
            name="serviceCenterUrl"
            defaultValue={props.data?.serviceCenterUrl}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Service Center Url"
                name={name}
                value={value}
                onChange={onChange}
                id="serviceCenterUrl"
              />
            )}
          />
          <ErrorMessage>{errors.serviceCenterUrl?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderButton = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          <button
            type="button"
            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
            disabled={props.isSubmitting}
          >
            Update
          </button>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(props.onSubmit)}>
      <div className="p-4">
        <h4 className="mb-3 font-weight-semibold">Update PDF Settings</h4>
        <div className="row gutter-10">
          {renderPrimaryColor()}
          {renderPaidCapitalAmount()}
          {renderRegistrationNumber()}
          {renderCompanyNameEnglish()}
          {renderCompanyNameArabic()}
          {renderPdfLogoUrl()}

          <ErrorMessage>{props.error}</ErrorMessage>
          {renderButton()}
        </div>
      </div>
    </form>
  );
};

export default UpdateForm;
